import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CircleAlert, LoaderCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import { isOutlookQuotationActionId, outlookQuotationDraftService, outlookQuotationOwnerIdentity } from '@/services/outlookQuotationDraftService';

function handoffError(cause: unknown): string {
  const status = Number((cause as { status?: number })?.status);
  if (status === 401 || status === 403) return 'Sign in with the same account that prepared this draft. Quotation access requires finance or admin permission.';
  if (status === 404) return 'This quotation draft is unavailable. Return to the original Outlook email and prepare it again.';
  if (status === 410) return 'This quotation draft has expired. Return to the original Outlook email and prepare a fresh draft.';
  return 'The quotation draft could not be loaded. Return to Outlook and try opening it again. Nothing has been created or sent.';
}

export default function OutlookQuotationHandoffPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const identity = outlookQuotationOwnerIdentity(user);
  const currentIdentity = useRef(identity);
  currentIdentity.current = identity;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    const params = new URLSearchParams(location.search);
    const actionId = params.get('actionId');
    if (!isOutlookQuotationActionId(actionId) || params.getAll('actionId').length !== 1 || [...params.keys()].some(key => key !== 'actionId')) {
      setError('A valid Outlook quotation reference is required. Open the draft from its original Outlook action.');
      return () => { active = false; };
    }
    if (!identity) {
      setError('Sign in with the same account that prepared this quotation draft.');
      return () => { active = false; };
    }
    void outlookQuotationDraftService.getDraft(actionId).then(draft => {
      if (!active || currentIdentity.current !== identity) return;
      if (Date.parse(draft.expiresAt) <= Date.now()) {
        setError(handoffError({ status: 410 }));
        return;
      }
      navigate('/quotations/workflow', {
        replace: true,
        state: {
          prefill: draft.prefill,
          outlookQuotationDraft: { ownerIdentity: identity, source: draft.source },
        },
      });
    }).catch(cause => {
      if (active && currentIdentity.current === identity) setError(handoffError(cause));
    });
    return () => { active = false; };
  }, [location.search, identity, navigate]);

  return <main style={{ minHeight: '70vh', display: 'grid', placeItems: 'center', padding: '24px' }}>
    <section style={{ width: 'min(520px, 100%)', minWidth: 0, padding: '24px', border: '1px solid #d8e0ee', borderRadius: '18px', background: '#fff', boxShadow: '0 18px 50px rgba(18,43,78,.12)', overflowWrap: 'anywhere' }}>
      {error ? <>
        <CircleAlert size={28} color="#a33b32" />
        <h1 style={{ margin: '16px 0 8px', fontSize: '24px' }}>Quotation draft needs attention</h1>
        <p role="alert" style={{ color: '#657187', lineHeight: 1.6 }}>{error}</p>
        <button type="button" onClick={() => navigate('/quotations')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '44px', padding: '10px 14px' }}>Open quotations <ArrowRight size={16} /></button>
      </> : <>
        <LoaderCircle size={28} color="#2456c8" />
        <h1 style={{ margin: '16px 0 8px', fontSize: '24px' }}>Opening quotation draft</h1>
        <p role="status" style={{ margin: 0, color: '#657187', lineHeight: 1.6 }}>Loading email details for your review. This does not create a quotation, upload attachments or send email.</p>
      </>}
    </section>
  </main>;
}
